import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { formatRelative } from "../utils/datetime";
import { AtRiskTicket, HealthSnapshot, Summary } from "../types";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  medium: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

type LicenseStatus = {
  status: string;
  licensed: boolean;
  tier?: string | null;
  expires_at?: string | null;
  caps?: { devices?: number | null };
  device_count?: number;
  device_cap_ok?: boolean;
};

const LICENSE_BANNER: Record<string, string> = {
  active:
    "bg-gray-50 border-gray-200 text-gray-600 dark:bg-gray-900/50 dark:border-gray-700 dark:text-gray-400",
  grace:
    "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300",
  expired:
    "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/40 dark:border-red-700 dark:text-red-300",
  revoked:
    "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/40 dark:border-red-700 dark:text-red-300",
};

function LicenseBanner({ lic }: { lic: LicenseStatus }) {
  // Hidden entirely when unlicensed (dev/internal) — only surfaces a real license.
  if (!lic.licensed || lic.status === "unlicensed") return null;
  const cls = LICENSE_BANNER[lic.status] || LICENSE_BANNER.active;
  const cap = lic.caps?.devices ?? null;
  const overCap = lic.device_cap_ok === false;
  const label: Record<string, string> = {
    active: "Licensed",
    grace: "License expired — in grace period (read-only soon)",
    expired: "License expired — appliance locked",
    revoked: "License revoked — appliance locked",
  };
  return (
    <div className={`mb-6 border rounded-lg px-4 py-2 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${cls}`}>
      <span className="font-semibold">{label[lic.status] || lic.status}</span>
      {lic.tier && <span>Tier: {lic.tier}</span>}
      {lic.expires_at && <span>Expires: {lic.expires_at.slice(0, 10)}</span>}
      {cap != null && (
        <span className={overCap ? "font-semibold text-red-700 dark:text-red-300" : ""}>
          Devices: {lic.device_count ?? "?"} / {cap}
          {overCap ? " — at cap" : ""}
        </span>
      )}
    </div>
  );
}

/* KPI tile accent. FOUR TONES, and colour MEANS something.
 *
 * These six tiles used to carry six hand-picked colours - blue, indigo,
 * green, red, orange, yellow - with no rule behind them. Blue and indigo
 * were barely distinguishable, and yellow on "Open Tickets" implied a
 * warning state that 57 open tickets simply is not. When every tile is a
 * different colour, colour tells the reader nothing; it is decoration
 * competing with the numbers.
 *
 * So: a count that is just a fact is NEUTRAL. Colour is reserved for
 * something being good, needing attention, or being wrong - and it is
 * DERIVED from the value, so a fleet with nothing offline does not show a
 * red tile claiming otherwise.
 */
type Tone = "neutral" | "good" | "warn" | "bad";

/* A BACKGROUND, not a border. This is not a style preference.
 *
 * The accent was `border-l-4 border-amber-500` on the same element as the
 * card's own `border border-gray-100`. Both set `border-color` at equal
 * specificity, so which one wins is decided by the ORDER Tailwind emits
 * them in - and it emits amber BEFORE gray but green and red AFTER:
 *
 *     border-amber-500  offset 19448   <- loses to gray-100
 *     border-gray-100   offset 20900
 *     border-green-500  offset 21832   <- beats gray-100
 *     border-red-500    offset 23526   <- beats gray-100
 *
 * So "Pending Reboot" rendered with NO accent while red and green worked,
 * and the DOM looked perfectly correct - the class was there. Caught by
 * sampling the pixel, not by reading the markup.
 *
 * background-color never fights border-color, so a strip is immune to the
 * emission order of the palette. */
const TONE_STRIP: Record<Tone, string> = {
  neutral: "bg-gray-300 dark:bg-gray-600",
  good: "bg-green-500",
  warn: "bg-amber-500",
  bad: "bg-red-500",
};

function Stat({
  label,
  value,
  tone = "neutral",
  sub,
  link,
}: {
  label: string;
  value: number;
  tone?: Tone;
  sub?: string;
  link?: string;
}) {
  const card = (
    /* h-full, and the Link below is a block that also stretches.
     *
     * Without it a tile carrying a `sub` line was TALLER than its
     * neighbours, so the first row of the dashboard sat unevenly - the one
     * thing a reader notices before reading a single number. The grid makes
     * the row equal height; the card has to agree to fill it. */
    <div
      className="relative h-full overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 pl-6 pr-5 py-5 hover:shadow-md transition-shadow"
    >
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-1 ${TONE_STRIP[tone]}`}
      />
      <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{value}</div>
      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
  return link ? (
    <Link to={link} className="block h-full">
      {card}
    </Link>
  ) : (
    card
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [atRisk, setAtRisk] = useState<AtRiskTicket[]>([]);
  const [health, setHealth] = useState<HealthSnapshot | null>(null);
  const [lic, setLic] = useState<LicenseStatus | null>(null);

  // Four requests per tick, so this is the screen that most deserves to stop
  // when nobody is looking at it. See useAutoRefresh.
  const load = useCallback(() => {
    api.get("/api/v1/reports/summary").then((r) => setSummary(r.data));
    api
      .get("/api/v1/sla/at-risk")
      .then((r) => setAtRisk(r.data))
      .catch(() => setAtRisk([]));
    api
      .get("/api/v1/reports/health-snapshot")
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null));
    api
      .get("/api/v1/license/status")
      .then((r) => setLic(r.data))
      .catch(() => setLic(null));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useAutoRefresh(load, 30000);

  if (!summary)
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 dark:text-gray-500">
        Loading...
      </div>
    );

  const isFresh = summary.total_customers === 0 && summary.total_devices === 0;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Dashboard</h1>
      {lic && <LicenseBanner lic={lic} />}
      {isFresh && <GettingStarted />}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* A count, not a condition. */}
        <Stat
          label="Total Customers"
          value={summary.total_customers}
          link="/customers"
        />
        <Stat label="Total Devices" value={summary.total_devices} link="/devices" />
        <Stat
          label="Online Now"
          value={summary.online_devices}
          tone="good"
          sub={`${summary.offline_devices} offline`}
          link="/devices?status=online"
        />
        {/* Red only when there IS something offline. A permanent red tile
            reading 0 is how an indicator stops being read at all. */}
        <Stat
          label="Offline"
          value={summary.offline_devices}
          tone={summary.offline_devices > 0 ? "bad" : "neutral"}
          link="/devices?status=offline"
        />
        <Stat
          label="Pending Reboot"
          value={summary.pending_reboot}
          tone={summary.pending_reboot > 0 ? "warn" : "neutral"}
          link="/devices"
        />
        {/* Open tickets are the WORK, not a fault. Amber here said
            "something is wrong" about a service desk doing its job. */}
        <Stat label="Open Tickets" value={summary.open_tickets} link="/tickets" />
      </div>

      {atRisk.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              SLA — at risk or breached
              <span className="ml-2 text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-2 py-0.5 rounded-full">
                {atRisk.length}
              </span>
            </h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {atRisk.slice(0, 5).map((t) => {
              const breached = t.pct_consumed >= 1.0;
              return (
                <Link
                  key={`${t.ticket_id}-${t.risk_kind}`}
                  to={`/tickets/${t.ticket_id}`}
                  className="flex items-center justify-between gap-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 -mx-2 px-2 rounded"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
                        PRIORITY_COLORS[t.priority] || "bg-gray-100 dark:bg-gray-700"
                      }`}
                    >
                      {t.priority}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        #{t.ticket_id} {t.title}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {t.customer_name || "Unassigned"} · {t.risk_kind}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className={`text-xs font-semibold ${
                        breached ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"
                      }`}
                    >
                      {breached ? "BREACHED" : "AT RISK"}
                    </div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                      {Math.round(t.elapsed_minutes)} / {t.target_minutes} min
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          {atRisk.length > 5 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              … and {atRisk.length - 5} more
            </div>
          )}
        </div>
      )}

      {health && <HealthSnapshotWidget snapshot={health} />}

      <CapacityForecastWidget />

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/onboarding"
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Onboard New Customer
          </Link>
          <Link
            to="/devices?status=offline"
            className="border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
          >
            View Offline Devices
          </Link>
          <Link
            to="/customers"
            className="border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
          >
            Manage Customers
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * First-run welcome. Shown on the dashboard while the appliance has no
 * customers or devices yet, so a new MSP sees a guided path instead of a
 * wall of zeros.
 */
function GettingStarted() {
  const steps = [
    {
      title: "Onboard your first customer",
      body: "Create the customer and generate a ready-to-run installer — the agent binary is built in, nothing to upload.",
    },
    {
      title: "Deploy the agent",
      body: "Run the installer on their devices. Each one enrolls itself and appears here automatically within a minute.",
    },
    {
      title: "Connect your tools (optional)",
      body: "Link Microsoft 365, cloud storage, EDR, and backup under Integrations to manage everything from one place.",
    },
  ];
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
        Welcome — let's get set up
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        No customers or devices yet. Three quick steps to a live console:
      </p>
      <ol className="space-y-4 mb-6">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
              {i + 1}
            </span>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.title}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{s.body}</div>
            </div>
          </li>
        ))}
      </ol>
      <Link
        to="/onboarding"
        className="inline-block bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Onboard your first customer →
      </Link>
    </div>
  );
}

/**
 * "What's broken right now" — consolidated alert/offline/remediation/network
 * view that powers the morning-standup glance. If every section is empty
 * we render a single "All clear" card so it's obvious the absence of data
 * means good news (not a load failure).
 */
function HealthSnapshotWidget({ snapshot }: { snapshot: HealthSnapshot }) {
  const allClear =
    snapshot.active_alerts.total === 0 &&
    snapshot.offline_devices.total === 0 &&
    snapshot.failed_remediation.length === 0 &&
    snapshot.network_down.length === 0 &&
    (snapshot.network_unmonitored?.length ?? 0) === 0;

  if (allClear) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          What's broken right now
        </h2>
        <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/40 px-3 py-2 rounded">
          All clear — no active alerts, offline devices, failed self-heals, or unreachable network
          gear.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        What's broken right now
      </h2>

      {/* Severity chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <SeverityChip
          label="Critical"
          count={snapshot.active_alerts.critical}
          color="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
        />
        <SeverityChip
          label="Warning"
          count={snapshot.active_alerts.warning}
          color="bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
        />
        <SeverityChip
          label="Info"
          count={snapshot.active_alerts.info}
          color="bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
        />
        {/* Acknowledged-but-open, kept visible and deliberately muted. The three
            chips above count only UNACKNOWLEDGED alerts, because acking is how a
            tech clears an alert from the active feed and counting them made this
            panel disagree with the page it links to. But they haven't resolved
            either, so they get their own chip rather than silently vanishing. */}
        {!!snapshot.active_alerts.acknowledged && (
          <Link
            to="/alerts?ack=1"
            className="text-xs px-2 py-1 rounded font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            title={`${snapshot.active_alerts.acknowledged} alert(s) acknowledged but not yet resolved`}
          >
            Ack'd: {snapshot.active_alerts.acknowledged}
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Offline devices */}
        <HealthSection
          title="Offline devices"
          count={snapshot.offline_devices.total}
          linkTo="/devices?status=offline"
          emptyMessage="All devices online."
        >
          {snapshot.offline_devices.recent.map((d) => (
            <Link
              key={d.device_id}
              to={`/devices/${d.device_id}`}
              className="block py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700/40 -mx-1 px-1 rounded"
            >
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{d.hostname}</div>
              <div className="text-gray-500 dark:text-gray-400 truncate">
                {d.customer_name || "Unassigned"}
                {d.last_seen && (
                  <span className="ml-1">
                    · {formatRelative(d.last_seen)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </HealthSection>

        {/* Failed remediation */}
        <HealthSection
          title="Failed self-heals (24h)"
          count={snapshot.failed_remediation.length}
          linkTo="/remediation"
          emptyMessage="No failures in the last 24 hours."
        >
          {snapshot.failed_remediation.slice(0, 5).map((e, idx) => (
            <div key={idx} className="py-1.5 text-xs">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {e.device_hostname || "—"}{" "}
                <span className="text-gray-400 dark:text-gray-500">· {e.action}</span>
              </div>
              <div className="text-gray-500 dark:text-gray-400 truncate">
                {e.customer_name || "Unassigned"} · {e.target || "—"}
              </div>
            </div>
          ))}
        </HealthSection>

        {/* Network devices that are genuinely down. A device which answered
            and refused our credentials is NOT one of these - it gets its own
            section below, because sending an engineer to a site for a
            credential mismatch is worse than not telling them at all. */}
        <HealthSection
          title="Network unreachable"
          count={snapshot.network_down.length}
          linkTo="/network"
          emptyMessage="All network gear reachable."
        >
          {snapshot.network_down.slice(0, 5).map((n) => (
            <div key={n.id} className="py-1.5 text-xs">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {n.name}{" "}
                <span className="text-gray-400 dark:text-gray-500 font-mono">({n.ip_address})</span>
              </div>
              <div className="text-gray-500 dark:text-gray-400 truncate">
                {n.customer_name || "Unassigned"} ·{" "}
                <span className="uppercase">{n.last_status}</span>
              </div>
            </div>
          ))}
        </HealthSection>

        {/* Up, but we cannot read them. Real work, but not an outage. */}
        <HealthSection
          title="Network gear we can't read"
          count={snapshot.network_unmonitored?.length ?? 0}
          linkTo="/network"
          emptyMessage="Every reachable device is being read."
        >
          {(snapshot.network_unmonitored ?? []).slice(0, 5).map((n) => (
            <div key={n.id} className="py-1.5 text-xs">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {n.name}{" "}
                <span className="text-gray-400 dark:text-gray-500 font-mono">({n.ip_address})</span>
              </div>
              <div className="text-gray-500 dark:text-gray-400 truncate">
                {n.customer_name || "Unassigned"} · {n.last_error || n.last_status}
              </div>
            </div>
          ))}
        </HealthSection>
      </div>
    </div>
  );
}

function SeverityChip({ label, count, color }: { label: string; count: number; color: string }) {
  const to = `/alerts?severity=${label.toLowerCase()}`;
  if (count === 0) {
    // Still clickable so you can jump to that (empty) filter, but muted.
    return (
      <Link
        to={to}
        className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
      >
        {label}: 0
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className={`text-xs px-2 py-1 rounded font-semibold hover:opacity-80 ${color}`}
      title={`View ${count} ${label.toLowerCase()} alert(s)`}
    >
      {label}: {count}
    </Link>
  );
}

function HealthSection({
  title,
  count,
  linkTo,
  emptyMessage,
  children,
}: {
  title: string;
  count: number;
  linkTo: string;
  emptyMessage: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-100 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <Link
          to={linkTo}
          className="text-xs font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
        >
          {title}
        </Link>
        {count > 0 && (
          <span className="text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded font-mono">
            {count}
          </span>
        )}
      </div>
      {count === 0 ? (
        <div className="text-xs text-gray-400 dark:text-gray-500">{emptyMessage}</div>
      ) : (
        <>{children}</>
      )}
    </div>
  );
}

type ForecastRow = {
  device_id: number;
  /** The machine's NAME. This table rendered device_id raw - "#85 is 97.9%
   *  full and fills on 2026-05-24" - and an id is not something a technician
   *  can act on without going to look it up first. */
  hostname?: string | null;
  customer_name?: string | null;
  metric: string;
  current_value: number;
  slope_per_day: number;
  threshold: number;
  already_over: boolean;
  days_to_threshold: number | null;
  projected_date: string | null;
};

function CapacityForecastWidget() {
  const [rows, setRows] = useState<ForecastRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fcSort = useSort("projected_date", "asc");

  useEffect(() => {
    api
      .get("/api/v1/forecast/at-risk", {
        params: { metric: "disk_used_pct", threshold: 90, horizon_days: 90, limit: 8 },
      })
      .then((r) => setRows(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Load failed"))
      .finally(() => setLoading(false));
  }, []);

  // Still fetching: render nothing rather than an empty-looking panel that
  // then fills in. This is the only one of the three that may be silent.
  if (loading) return null;

  // A FAILURE MUST NOT LOOK LIKE GOOD NEWS. This used to `return null` on
  // error, so a 500 from the forecast endpoint made the panel vanish and the
  // reader concluded there were no disks about to fill. Absent is not zero —
  // the same rule the reports engine follows, applied to the screen.
  if (error)
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Capacity forecast
        </h2>
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Could not load the disk forecast, so this is <strong>not</strong> a
          statement that nothing is filling up — it is unknown right now. {error}
        </p>
      </div>
    );

  // And say so when the answer is genuinely "nothing". Good news is worth
  // stating: an absent panel is indistinguishable from a panel that broke,
  // and the "Network unreachable" card beside it already says "All network
  // gear reachable." rather than disappearing.
  if (rows.length === 0)
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Capacity forecast
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No disks are projected to reach 90% within the next 90 days.
        </p>
      </div>
    );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Capacity forecast — disks projected to fill in &lt;90 days
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          linear regression on daily snapshots
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
          <tr>
            <SortableTh label="Device" sortKey="device_id" sort={fcSort.sort} onSort={fcSort.toggle} />
            <SortableTh label="Current" sortKey="current_value" sort={fcSort.sort} onSort={fcSort.toggle} align="right" />
            <SortableTh label="Growth/day" sortKey="slope_per_day" sort={fcSort.sort} onSort={fcSort.toggle} align="right" />
            <th className="text-right py-1.5">Days to 90%</th>
            <SortableTh label="Projected fill" sortKey="projected_date" sort={fcSort.sort} onSort={fcSort.toggle} align="right" />
          </tr>
        </thead>
        <tbody>
          {sortRows(rows, fcSort.sort, (r, key) => {
            switch (key) {
              case "current_value": return r.current_value;
              case "slope_per_day": return r.slope_per_day;
              case "projected_date": return r.projected_date ? Date.parse(r.projected_date) : 0;
              // Sort by what is DISPLAYED. Sorting by id under a column
              // that shows hostnames just looks broken.
              default: return (r.hostname || `#${r.device_id}`).toLowerCase();
            }
          }).map((r) => (
            <tr
              key={`${r.device_id}-${r.metric}`}
              className="border-b dark:border-gray-700 last:border-0 text-gray-900 dark:text-gray-100"
            >
              <td className="py-1.5 pr-3">
                <Link
                  to={`/devices/${r.device_id}`}
                  className="text-brand dark:text-brand-light hover:underline font-medium"
                >
                  {r.hostname || `#${r.device_id}`}
                </Link>
                {r.customer_name && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[14rem]">
                    {r.customer_name}
                  </div>
                )}
              </td>
              <td className="py-1.5 text-right font-mono text-xs">
                {r.current_value.toFixed(1)}%
              </td>
              <td
                className={`py-1.5 text-right font-mono text-xs ${
                  r.slope_per_day > 0.1
                    ? "text-red-700 dark:text-red-300"
                    : "text-gray-700 dark:text-gray-300"
                }`}
              >
                {r.slope_per_day >= 0 ? "+" : ""}
                {r.slope_per_day.toFixed(3)}%
              </td>
              <td className="py-1.5 text-right font-mono text-xs">
                {r.already_over ? (
                  <span className="text-red-700 dark:text-red-300 font-bold">over</span>
                ) : r.days_to_threshold === null ? (
                  "—"
                ) : (
                  Math.round(r.days_to_threshold)
                )}
              </td>
              <td className="py-1.5 text-right text-xs">{r.projected_date || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
